import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Id } from "../../../../convex/_generated/dataModel";
import {
  saveEditorState,
  loadEditorState
} from "../utils/general/indexdb-helper";
import { debounce } from "../utils/general/debounce";
import {
  resolveOverlaysForSave,
  hasUnresolvedBlobUrls,
  getPendingUploadCount
} from "../utils/general/overlay-url-resolver";

interface AutosaveOptions {
  /**
   * Interval in milliseconds between autosaves
   * @default 5000 (5 seconds)
   */
  interval?: number;

  /**
   * Function to call when an autosave is loaded
   */
  onLoad?: (data: any) => void;

  /**
   * Function to call when an autosave is saved
   */
  onSave?: () => void;

  /**
   * Whether a project is currently loading from URL
   * If true, autosave will wait before loading from IndexedDB
   */
  isLoadingProject?: boolean;
}

/**
 * Hook for automatically saving editor state to IndexedDB
 *
 * @param projectId Unique identifier for the project
 * @param state Current state to be saved
 * @param options Configuration options for autosave behavior
 * @returns Object with functions to manually save and load state
 */
export const useAutosave = (
  projectId: string,
  state: any,
  options: AutosaveOptions = {}
) => {
  const { interval = 5000, onLoad, onSave, isLoadingProject = false } = options;

  const timerRef = useRef<any | null>(null);
  const lastSavedStateRef = useRef<string>("");
  const lastConvexSyncRef = useRef<string>("");
  const [hasCheckedForAutosave, setHasCheckedForAutosave] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Convex mutation for cross-device sync
  const saveEditorOverlays = useMutation(api.projects.saveEditorOverlays);

  // Debounced Convex sync (5 seconds as per plan)
  const syncToConvex = useMemo(
    () =>
      debounce(async (currentState: any, currentProjectId: string) => {
        // Skip if not a real project
        if (!currentProjectId || currentProjectId === "TestComponent") return;

        // Check if it looks like a valid Convex ID
        if (!currentProjectId.match(/^[a-z0-9]+$/i)) {
          console.log('[Autosave] Skipping Convex sync - invalid project ID format');
          return;
        }

        try {
          // Resolve blob URLs to Convex URLs before saving
          // This ensures saved overlays have valid, persistent URLs
          const overlays = currentState.overlays || [];
          const { overlays: resolvedOverlays, resolvedCount, unresolvedCount } =
            await resolveOverlaysForSave(overlays);

          if (resolvedCount > 0) {
            console.log(`[Autosave] Resolved ${resolvedCount} blob URL(s) before Convex sync`);
          }
          if (unresolvedCount > 0) {
            console.log(`[Autosave] ${unresolvedCount} overlay(s) still have pending uploads`);
          }

          const overlaysJson = JSON.stringify(resolvedOverlays);
          const aspectRatio = currentState.aspectRatio || "9:16";

          // Skip if nothing changed since last sync
          const syncKey = `${overlaysJson}:${aspectRatio}`;
          if (syncKey === lastConvexSyncRef.current) {
            return;
          }

          await saveEditorOverlays({
            projectId: currentProjectId as Id<"projects">,
            overlaysJson,
            aspectRatio,
          });

          lastConvexSyncRef.current = syncKey;
          console.log('[Autosave] Synced to Convex');
        } catch (error) {
          console.error('[Autosave] Convex sync failed:', error);
          // Don't throw - Convex sync failure shouldn't break local autosave
        }
      }, 5000),
    [saveEditorOverlays]
  );

  // Automatically load saved state on mount, but only once and after project loading completes
  useEffect(() => {
    const autoLoadState = async () => {
      if (hasCheckedForAutosave) return;

      // Wait for project loading to complete before loading from IndexedDB
      // This ensures project overlays take precedence over saved state
      if (isLoadingProject) {
        console.log('[Autosave] Waiting for project to load before checking IndexedDB...');
        return;
      }

      try {
        console.log('[Autosave] Checking for saved state in IndexedDB...');
        const loadedState = await loadEditorState(projectId);
        
        if (loadedState && onLoad) {
          console.log('[Autosave] Restored saved state from IndexedDB');
          onLoad(loadedState);
        } else {
          console.log('[Autosave] No saved state found in IndexedDB');
        }
        setHasCheckedForAutosave(true);
        setIsInitialLoadComplete(true);
      } catch (error) {
        console.error('[Autosave] Failed to auto-load state:', error);
        // Always complete the load even if it fails to prevent infinite loading
        setHasCheckedForAutosave(true);
        setIsInitialLoadComplete(true);
      }
    };

    autoLoadState();
  }, [projectId, onLoad, hasCheckedForAutosave, isLoadingProject]);

  // Set up autosave timer
  useEffect(() => {
    // Don't start autosave if projectId is not valid
    if (!projectId) return;

    const saveIfChanged = async () => {
      const currentStateString = JSON.stringify(state);

      // Only save if state has changed since last save
      if (currentStateString !== lastSavedStateRef.current) {
        try {
          // Save to IndexedDB (local)
          await saveEditorState(projectId, state);
          lastSavedStateRef.current = currentStateString;
          if (onSave) onSave();

          // Also trigger Convex sync (debounced, will dedupe rapid calls)
          syncToConvex(state, projectId);
        } catch (error) {
          console.error('[Autosave] Save failed:', error);
        }
      }
    };

    // Set up interval for autosave
    timerRef.current = setInterval(saveIfChanged, interval);

    // Clean up timer on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [projectId, state, interval, onSave, syncToConvex]);

  // Beforeunload handler to warn about unresolved blob URLs
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const overlays = state?.overlays || [];
      if (hasUnresolvedBlobUrls(overlays)) {
        const pendingCount = getPendingUploadCount(overlays);
        const message = `You have ${pendingCount} media file(s) still uploading. If you leave now, these files may not be saved to your project.`;
        e.preventDefault();
        // Modern browsers will show a generic message, but we set returnValue for compatibility
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state]);

  // Function to manually save state
  const saveState = async () => {
    try {
      await saveEditorState(projectId, state);
      lastSavedStateRef.current = JSON.stringify(state);
      if (onSave) onSave();

      // Also trigger Convex sync
      syncToConvex(state, projectId);

      return true;
    } catch (error) {
      console.error('[Autosave] Manual save failed:', error);
      return false;
    }
  };

  // Function to manually load state
  const loadState = async () => {
    try {
      const loadedState = await loadEditorState(projectId);
      if (loadedState && onLoad) {
        onLoad(loadedState);
      }
      return loadedState;
    } catch (error) {
      console.error('[Autosave] Manual load failed:', error);
      return null;
    }
  };

  return {
    saveState,
    loadState,
    isInitialLoadComplete,
  };
};
